import { useLocation, useParams } from "react-router-dom";
import { useRef, useState, useEffect, lazy, Suspense, useCallback } from "react";
import axios from "axios";
import RestSidebar from "../Panels/RestaurantPanel/Components/RestSidebar";
import Header from "../Panels/Components/Header";
import RestDashboard from "../Panels/RestaurantPanel/RestaurantDashboard/RestDashboard";
import StockView from "../Panels/RestaurantPanel/StockManagement/StockView";
import StockManage from "../Panels/RestaurantPanel/StockManagement/StockManage";
import OrderManagement from "../Panels/RestaurantPanel/OrderManagement/OrderManagement";
import PastOrder from "../Panels/RestaurantPanel/PastOrder";
import RestaurantUser from "../Panels/RestaurantPanel/RestaurantUser/RestaurantUser";
import RestUserAdd from "../Panels/RestaurantPanel/RestaurantUser/RestUserAdd";
import WineListManagement from "../Panels/RestaurantPanel/WineListManagement/WineListManagement";
import RestUserRole from "../Panels/RestaurantPanel/RestaurantUserRole/RestUserRole";
import RestSettings from "../Panels/RestaurantPanel/RestaurantSettings/RestSettings";
import RestUserSettings from "../Panels/RestaurantPanel/RestaurantSettings/RestUserSettings";
import RestSettingsDetail from "../Panels/RestaurantPanel/RestaurantSettings/RestSettingsDetail";
import ChangePassword from "../Panels/Components/ChangePassword";
import DishManagement from "../Panels/RestaurantPanel/DishManagement/DishManagement";
import WineFood from "../Panels/RestaurantPanel/WineFood";
import MenuManagement from "../Panels/RestaurantPanel/MenuManagement/MenuManagement";
import BeverageManagement from "../Panels/RestaurantPanel/BeverageManagement/BeverageManagement";
import CourseManagement from "../Panels/RestaurantPanel/CourseManagement/CourseManagement";
import CategoryManagement from "../Panels/RestaurantPanel/CategoryManagement/CategoryManagement";

// Lazy load PageNotFound
const PageNotFound = lazy(() => import("../Pages/PageNotFound"));
const RestaurantRoutes = () => {
  const { rest_name, rest_slug2 } = useParams();
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;
  const location = useLocation();
  const isUserRoute = location.pathname.startsWith("/restaurantPanel/");
  const showSidebar = isUserRoute && location.pathname !== "/admin/login";

  // Ref to access the Incoming Order component's function
  const incomingOrderRef = useRef();
  const pastOrderRef = useRef();
  const restaurantWineListRef = useRef();
  const restUserRef = useRef();
  const userRoleRef = useRef();
  const dishManagementRef = useRef();
  const courseManagementRef = useRef();
  const menuRef = useRef();
  const beverageRef = useRef();
  const dropdownRef = useRef();

  const [showSidebarForMobile, setShowSidebarForMobile] = useState(false);
  const [checkPermission, setCheckPermission] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  const [logo, setLogo] = useState(null);
  const [userName, setUserName] = useState("");
  const [userType, setUserType] = useState(() => localStorage.getItem("Role") || "");
  const [userID, setUserID] = useState("");
  const role = localStorage.getItem("Role");
  const roleType = localStorage.getItem("type");
  const roleId = localStorage.getItem("loginUserId");
  const currentRestId = localStorage.getItem("currentRestId");

  // Functions to toggle the state in Incoming from Sidebar
  const incomingOrder = () => {
    if (incomingOrderRef.current) {
      incomingOrderRef.current.incomingOrder();
    }
  };
  const pastOrder = () => {
    if (pastOrderRef.current) {
      pastOrderRef.current.pastOrder();
    }
  };
  const restaurantWineList = () => {
    if (restaurantWineListRef.current) {
      restaurantWineListRef.current.restaurantWineList();
    }
  };
  const userRole = () => {
    if (userRoleRef.current) {
      userRoleRef.current.userRole();
    }
  };
  const dishManagement = () => {
    if (dishManagementRef.current) {
      dishManagementRef.current.dishManagement();
    }
  };
  const courseManagement = () => {
    if (courseManagementRef.current) {
      courseManagementRef.current.courseManagement();
    }
  };
  const restUser = () => {
    if (restUserRef.current) {
      restUserRef.current.restUser();
    }
  };
  const menu = () => {
    if (menuRef.current) {
      menuRef.current.menu();
    }
  };
  const beverage = () => {
    if (beverageRef.current) {
      beverageRef.current.beverage();
    }
  };
  const dropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current.dropdown();
    }
  };
  const closeModal = () => {
    incomingOrder();
    pastOrder();
    restaurantWineList();
    userRole();
    restUser();
    dishManagement();
    courseManagement();
    menu();
    beverage();
    setShowSidebarForMobile(false);
  }
  const hasPermission = (moduleName, action) => {
    const module = checkPermission?.find(item => item.module === moduleName);
    if (!module) {
      return false;
    }
    return module.actions[action] || false;
  }
  const getPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/roles/${localStorage.getItem("RoleId")}/show-permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCheckPermission(response?.data?.permissions);
    } catch (error) {
      console.error("Error fetching user role data:", error);
    } finally {
      setLoadingPermissions(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    getPermissions();
  }, [rest_name, rest_slug2, getPermissions]);

  useEffect(() => {
    setUserType(role);
    if (roleType === 'restaurant-owner') {
      setUserID(currentRestId);
    } else {
      setUserID(roleId);
    }
  }, [role, roleType, currentRestId, roleId]);

  const fetchAndUpdateLogo = useCallback(async () => {
    try {
      if (!userID) {
        setLogo(null);
        setUserName("");
        return;
      }

      let responseProfileData;

      if (userType === "restaurant" && roleType === 'restaurant-owner') {
        responseProfileData = await axios.get(`${apiUrl}/api/v1/restaurants/${userID}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else if (userType === "restaurant" && roleType === 'restaurant-user') {
        responseProfileData = await axios.get(`${apiUrl}/api/v1/users/${userID}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      const completeLogoUrl = responseProfileData?.data?.logo || responseProfileData?.data?.avatar
        ? `${logoUrl}/${responseProfileData?.data?.logo || responseProfileData?.data?.avatar}`
        : null;

      setLogo(completeLogoUrl);
      // console.log("🚀 ~ RestaurantRoutes ~ completeLogoUrl:", completeLogoUrl);
      setUserName(responseProfileData?.data?.name || "");
    } catch (error) {
      console.error('Error fetching Profile Settings data:', error);
    }
  }, [userType, roleType, apiUrl, userID, token, logoUrl]);

  useEffect(() => {
    fetchAndUpdateLogo();
  }, [userType, userID, fetchAndUpdateLogo]);

  return (
    loadingPermissions ? (
      <div className="flex justify-center items-center h-screen" >
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#8B2433]"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    ) : (
      <div className="flex bg-[#FFFFFF] relative h-screen w-full gap-[2%] sm:p-5 px-0">
        {showSidebar && (
          <RestSidebar
            showSidebarForMobile={showSidebarForMobile}
            setShowSidebarForMobile={setShowSidebarForMobile}
            closeModal={closeModal}
            hasPermission={hasPermission}
            ref={dropdownRef}
          />
        )}
        <div className="flex flex-col w-full h-full overflow-y-scroll p-5">
          <Header
            showSidebarForMobile={showSidebarForMobile}
            setShowSidebarForMobile={setShowSidebarForMobile}
            logo={logo}
            userName={userName}
          />
          <Suspense
            fallback={
              <div
                role="status"
                className="flex my-20 justify-center items-center h-full"
              >
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#8B2433]"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            }
          >
            {rest_slug2 && rest_name === "dashboard" && (hasPermission("Dashboard", "view") ? <RestDashboard hasPermission={hasPermission} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "viewStock" && (hasPermission("WineStock", "view") ? <StockView /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "decreaseStock" && ((hasPermission("WineStock", "add") || hasPermission("WineStock", "remove")) ? <StockManage hasPermission={hasPermission} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "incomingOrder" && (hasPermission("Orders", "view") ? <OrderManagement ref={incomingOrderRef} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "pastOrder" && (hasPermission("Orders", "view") ? <PastOrder ref={pastOrderRef} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "restaurant-user" && (hasPermission("Users", "view") ? <RestaurantUser ref={restUserRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "restaurant-user-add" && (hasPermission("Users", "add") ? <RestUserAdd /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "restaurantManagement" && (hasPermission("WineList", "view") ? <WineListManagement hasPermission={hasPermission} ref={restaurantWineListRef} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "userRole" && (hasPermission("Roles", "view") ? <RestUserRole ref={userRoleRef} hasPermission={hasPermission} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "settings" && (roleType === "restaurant-owner" ? <RestSettings dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} /> : <RestUserSettings dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} />)}
            {/* {rest_slug2 && rest_name === "settingsDetail" && (roleName === "Owner" ? <RestSettingsDetail dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} /> : <RestSettingsDetail dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} />)} */}
            {rest_slug2 && rest_name === "settingsDetail" && (hasPermission("Restaurant_info", "view") ? <RestSettingsDetail dropdown={dropdown} refreshLogo={fetchAndUpdateLogo} hasPermission={hasPermission} /> : <PageNotFound />)}
            {rest_slug2 && rest_name === "changePassword" && <ChangePassword dropdown={dropdown} />}
            {rest_slug2 && rest_name === "dishManagement" && <DishManagement ref={dishManagementRef} />}
            {rest_slug2 && rest_name === "wineFood" && <WineFood />}
            {rest_slug2 && rest_name === "menuManagement" && <MenuManagement ref={menuRef} />}
            {rest_slug2 && rest_name === "beverageManagement" && <BeverageManagement ref={beverageRef} />}
            {rest_slug2 && rest_name === "courseManagement" && <CourseManagement ref={courseManagementRef} />}
            {rest_slug2 && rest_name === "categoryManagement" && <CategoryManagement />}
          </Suspense>
        </div>
      </div>
    )
  );
};

export default RestaurantRoutes;